<script setup lang="ts">
import AdminDashboardKpiGrid from '@/components/admindashboard/AdminDashboardKpiGrid.vue'
import AdminPeriodSelector from '@/components/admindashboard/AdminPeriodSelector.vue'
import AdminTimeSeriesChart from '@/components/admindashboard/AdminTimeSeriesChart.vue'
import { useAdminDashboard } from '@/composables/useAdminDashboard'
import { useAdminPeriod } from '@/composables/useAdminPeriod'
import type { ChartDisplayMode } from '@/utils/adminDashboardUtils'
import SelectButton from 'primevue/selectbutton'
import Skeleton from 'primevue/skeleton'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const {
  periodType,
  monthOptions,
  quarterOptions,
  yearOptions,
  selectedMonth,
  selectedQuarter,
  selectedYear,
  customStartDate,
  customEndDate,
  periodRange,
  periodDurationDays
} = useAdminPeriod()

const { loading, totals, series, chartDisplayMode } = useAdminDashboard(periodRange, periodDurationDays)

const chartDisplayOptions = computed(() => [
  { label: t('adminDashboard.chartDisplay.period'), value: 'period' as ChartDisplayMode },
  { label: t('adminDashboard.chartDisplay.cumulative'), value: 'cumulative' as ChartDisplayMode }
])
</script>

<template>
  <div class="admin-dashboard">
    <header class="dashboard-header">
      <div>
        <h1 class="dashboard-title">{{ t('adminDashboard.title') }}</h1>
        <p v-if="!loading" class="period-label">{{ periodRange.label }}</p>
      </div>
      <AdminPeriodSelector
        v-model:period-type="periodType"
        v-model:selected-month="selectedMonth"
        v-model:selected-quarter="selectedQuarter"
        v-model:selected-year="selectedYear"
        v-model:custom-start-date="customStartDate"
        v-model:custom-end-date="customEndDate"
        :month-options="monthOptions"
        :quarter-options="quarterOptions"
        :year-options="yearOptions"
      />
    </header>

    <div v-if="loading" class="loading-section">
      <div class="grid">
        <div v-for="i in 4" :key="`kpi-${i}`" class="col-12 md:col-3">
          <Skeleton height="100px" class="mb-3" />
        </div>
      </div>
      <Skeleton height="48px" class="mb-3" />
      <Skeleton v-for="i in 4" :key="`chart-${i}`" height="380px" class="mb-3" />
    </div>

    <template v-else>
      <AdminDashboardKpiGrid :totals="totals" />

      <div class="chart-display-toggle">
        <span class="toggle-label">{{ t('adminDashboard.chartDisplay.label') }}</span>
        <SelectButton
          v-model="chartDisplayMode"
          :options="chartDisplayOptions"
          optionLabel="label"
          optionValue="value"
          :allowEmpty="false"
        />
      </div>

      <div class="charts-section">
        <AdminTimeSeriesChart
          class="chart-item"
          :title="t('adminDashboard.charts.employees.title')"
          :subtitle="t('adminDashboard.charts.employees.subtitle')"
          :buckets="series.employees"
          :display-mode="chartDisplayMode"
        />
        <AdminTimeSeriesChart
          class="chart-item"
          :title="t('adminDashboard.charts.companies.title')"
          :subtitle="t('adminDashboard.charts.companies.subtitle')"
          :buckets="series.companies"
          :display-mode="chartDisplayMode"
        />
        <AdminTimeSeriesChart
          class="chart-item"
          :title="t('adminDashboard.charts.deliveryNotes.title')"
          :subtitle="t('adminDashboard.charts.deliveryNotes.subtitle')"
          :buckets="series.deliveryNotes"
          :display-mode="chartDisplayMode"
        />
        <AdminTimeSeriesChart
          class="chart-item"
          :title="t('adminDashboard.charts.aiRequests.title')"
          :subtitle="t('adminDashboard.charts.aiRequests.subtitle')"
          :buckets="series.aiRequests"
          :display-mode="chartDisplayMode"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.admin-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: var(--p-text-color);
}

.period-label {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
}

.loading-section {
  padding-top: 0.5rem;
}

.chart-display-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.toggle-label {
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
  font-weight: 500;
}

.charts-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.chart-item {
  width: 100%;
}

@media (max-width: 768px) {
  .admin-dashboard {
    padding: 1rem;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
